/**
 * Demographic Aggregation Utilities
 *
 * Functions for aggregating cohort data into demographic breakdowns,
 * ideology averages, and salience statistics for the Voter Demographics dashboard.
 */

import type {
  Cohort,
  District,
  Region,
  AgeBracket,
  EducationLevel,
  IncomeLevel,
  SettlementType,
  IdeologyVector,
} from '@/types'
import { IDEOLOGY_AXES } from '@/constants/ideology'

/**
 * Population breakdown by demographic category
 */
export interface DemographicBreakdown {
  age: Record<AgeBracket, number>
  education: Record<EducationLevel, number>
  income: Record<IncomeLevel, number>
  settlement: Record<SettlementType, number>
  religion: Record<string, number>
  totalPopulation: number
  cohortCount: number
}

/**
 * Population-weighted ideology aggregate
 */
export interface IdeologyAggregate {
  averageVector: IdeologyVector
  averageSalience: IdeologyVector
  totalPopulation: number
}

/**
 * Salience data for a single axis
 */
export interface SalienceData {
  index: number
  key: string
  label: string
  value: number
  percentage: number
}

/**
 * Key statistics for the nation
 */
export interface NationKeyStats {
  totalPopulation: number
  cohortCount: number
  regionCount: number
  districtCount: number
  averageBaseTurnout: number
  largestDemographic: {
    category: string
    value: string
    population: number
    percentage: number
  }
  mostSalientIssue: {
    label: string
    value: number
  }
}

/**
 * Regional demographic summary
 */
export interface RegionalSummary {
  region: Region
  totalPopulation: number
  cohortCount: number
  districtCount: number
  dominantAge: AgeBracket
  dominantEducation: EducationLevel
  dominantIncome: IncomeLevel
  dominantSettlement: SettlementType
  districts: DistrictSummary[]
}

/**
 * District demographic summary
 */
export interface DistrictSummary {
  district: District
  totalPopulation: number
  cohortCount: number
  dominantAge: AgeBracket
  dominantEducation: EducationLevel
  dominantIncome: IncomeLevel
}

/**
 * Cohort with district info for regional grouping
 */
export interface CohortWithDistrict extends Cohort {
  district?: {
    id: string
    region_id: string
    settlement_type: SettlementType
  }
}

/**
 * Aggregate cohorts into demographic breakdown
 */
export function aggregateDemographics(
  cohorts: Cohort[],
  districts: District[]
): DemographicBreakdown {
  const districtMap = new Map(districts.map((d) => [d.id, d]))

  const breakdown: DemographicBreakdown = {
    age: { youth: 0, young_adult: 0, middle_aged: 0, senior: 0 },
    education: { low: 0, medium: 0, high: 0 },
    income: { low: 0, middle: 0, high: 0 },
    settlement: { rural: 0, suburban: 0, urban: 0 },
    religion: {},
    totalPopulation: 0,
    cohortCount: cohorts.length,
  }

  for (const cohort of cohorts) {
    const pop = cohort.population
    breakdown.totalPopulation += pop
    breakdown.age[cohort.age_bracket] += pop
    breakdown.education[cohort.education_level] += pop
    breakdown.income[cohort.income_level] += pop

    // Get settlement type from district
    const district = districtMap.get(cohort.district_id)
    if (district) {
      breakdown.settlement[district.settlement_type] += pop
    }

    // Religion
    const religion = cohort.religion || 'None'
    breakdown.religion[religion] = (breakdown.religion[religion] || 0) + pop
  }

  return breakdown
}

/**
 * Calculate population-weighted average ideology vector
 */
export function aggregateIdeology(cohorts: Cohort[]): IdeologyAggregate {
  if (cohorts.length === 0) {
    return {
      averageVector: [0, 0, 0, 0, 0, 0, 0] as IdeologyVector,
      averageSalience: [0, 0, 0, 0, 0, 0, 0] as IdeologyVector,
      totalPopulation: 0,
    }
  }

  let totalPopulation = 0
  const sumVector: number[] = [0, 0, 0, 0, 0, 0, 0]
  const sumSalience: number[] = [0, 0, 0, 0, 0, 0, 0]

  for (const cohort of cohorts) {
    const pop = cohort.population
    totalPopulation += pop

    for (let i = 0; i < 7; i++) {
      sumVector[i] += cohort.ideology_vector[i] * pop
      sumSalience[i] += cohort.salience_weights[i] * pop
    }
  }

  const averageVector = sumVector.map((sum) =>
    totalPopulation > 0 ? Math.round(sum / totalPopulation) : 0
  ) as IdeologyVector

  const averageSalience = sumSalience.map((sum) =>
    totalPopulation > 0 ? sum / totalPopulation : 0
  ) as IdeologyVector

  return { averageVector, averageSalience, totalPopulation }
}

/**
 * Convert salience weights to display data
 */
export function getSalienceData(salience: IdeologyVector): SalienceData[] {
  const maxSalience = Math.max(...salience, 0.01) // Prevent division by zero

  return IDEOLOGY_AXES.map((axis, index) => ({
    index,
    key: axis.key,
    label: axis.label,
    value: salience[index],
    percentage: Math.round((salience[index] / maxSalience) * 100),
  })).sort((a, b) => b.value - a.value)
}

/**
 * Calculate key statistics for the nation
 */
export function calculateKeyStats(
  cohorts: Cohort[],
  districts: District[],
  regions: Region[],
  breakdown: DemographicBreakdown,
  ideology: IdeologyAggregate
): NationKeyStats {
  // Calculate average base turnout (population-weighted)
  let totalTurnoutWeight = 0
  let turnoutSum = 0
  for (const cohort of cohorts) {
    turnoutSum += cohort.base_turnout * cohort.population
    totalTurnoutWeight += cohort.population
  }
  const averageBaseTurnout =
    totalTurnoutWeight > 0 ? turnoutSum / totalTurnoutWeight : 0

  // Find largest demographic group across all categories
  const demographicGroups: { category: string; value: string; population: number }[] = []

  // Age groups
  for (const [value, pop] of Object.entries(breakdown.age)) {
    demographicGroups.push({ category: 'Age', value: formatAgeBracket(value as AgeBracket), population: pop })
  }

  // Education groups
  for (const [value, pop] of Object.entries(breakdown.education)) {
    demographicGroups.push({ category: 'Education', value: formatEducationLevel(value as EducationLevel), population: pop })
  }

  // Income groups
  for (const [value, pop] of Object.entries(breakdown.income)) {
    demographicGroups.push({ category: 'Income', value: formatIncomeLevel(value as IncomeLevel), population: pop })
  }

  // Settlement groups
  for (const [value, pop] of Object.entries(breakdown.settlement)) {
    demographicGroups.push({ category: 'Settlement', value: formatSettlementType(value as SettlementType), population: pop })
  }

  demographicGroups.sort((a, b) => b.population - a.population)
  const largest = demographicGroups[0] || { category: 'N/A', value: 'N/A', population: 0 }

  // Find most salient issue
  const salienceData = getSalienceData(ideology.averageSalience)
  const mostSalient = salienceData[0] || { label: 'N/A', value: 0 }

  return {
    totalPopulation: breakdown.totalPopulation,
    cohortCount: cohorts.length,
    regionCount: regions.length,
    districtCount: districts.length,
    averageBaseTurnout,
    largestDemographic: {
      ...largest,
      percentage:
        breakdown.totalPopulation > 0
          ? Math.round((largest.population / breakdown.totalPopulation) * 100)
          : 0,
    },
    mostSalientIssue: {
      label: mostSalient.label,
      value: mostSalient.value,
    },
  }
}

/**
 * Group cohorts by region and calculate regional summaries
 */
export function calculateRegionalSummaries(
  cohorts: Cohort[],
  districts: District[],
  regions: Region[]
): RegionalSummary[] {
  // Create lookup maps
  const districtMap = new Map(districts.map((d) => [d.id, d]))
  const regionDistrictMap = new Map<string, District[]>()
  const regionCohortMap = new Map<string, Cohort[]>()

  // Group districts by region
  for (const district of districts) {
    const list = regionDistrictMap.get(district.region_id) || []
    list.push(district)
    regionDistrictMap.set(district.region_id, list)
  }

  // Group cohorts by region (via district)
  for (const cohort of cohorts) {
    const district = districtMap.get(cohort.district_id)
    if (district) {
      const list = regionCohortMap.get(district.region_id) || []
      list.push(cohort)
      regionCohortMap.set(district.region_id, list)
    }
  }

  return regions.map((region) => {
    const regionCohorts = regionCohortMap.get(region.id) || []
    const regionDistricts = regionDistrictMap.get(region.id) || []

    const breakdown = aggregateDemographics(regionCohorts, regionDistricts)

    return {
      region,
      totalPopulation: breakdown.totalPopulation,
      cohortCount: regionCohorts.length,
      districtCount: regionDistricts.length,
      dominantAge: getDominant(breakdown.age) as AgeBracket,
      dominantEducation: getDominant(breakdown.education) as EducationLevel,
      dominantIncome: getDominant(breakdown.income) as IncomeLevel,
      dominantSettlement: getDominant(breakdown.settlement) as SettlementType,
      districts: regionDistricts.map((district) => {
        const districtCohorts = regionCohorts.filter(
          (c) => c.district_id === district.id
        )
        const districtBreakdown = aggregateDemographics(districtCohorts, [district])

        return {
          district,
          totalPopulation: districtBreakdown.totalPopulation,
          cohortCount: districtCohorts.length,
          dominantAge: getDominant(districtBreakdown.age) as AgeBracket,
          dominantEducation: getDominant(districtBreakdown.education) as EducationLevel,
          dominantIncome: getDominant(districtBreakdown.income) as IncomeLevel,
        }
      }),
    }
  })
}

/**
 * Get the dominant (highest population) key from a record
 */
function getDominant<T extends string>(record: Record<T, number>): T {
  let maxKey: T | null = null
  let maxValue = -1

  for (const [key, value] of Object.entries(record) as [T, number][]) {
    if (value > maxValue) {
      maxValue = value
      maxKey = key
    }
  }

  return maxKey!
}

/**
 * Format population number with locale formatting
 */
export function formatPopulation(value: number): string {
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`
  }
  if (value >= 1_000) {
    return `${(value / 1_000).toFixed(1)}K`
  }
  return value.toLocaleString()
}

/**
 * Format age bracket for display
 */
export function formatAgeBracket(bracket: AgeBracket): string {
  const labels: Record<AgeBracket, string> = {
    youth: 'Youth (18-25)',
    young_adult: 'Young Adult (26-40)',
    middle_aged: 'Middle Aged (41-60)',
    senior: 'Senior (60+)',
  }
  return labels[bracket]
}

/**
 * Format education level for display
 */
export function formatEducationLevel(level: EducationLevel): string {
  const labels: Record<EducationLevel, string> = {
    low: 'Low Education',
    medium: 'Medium Education',
    high: 'High Education',
  }
  return labels[level]
}

/**
 * Format income level for display
 */
export function formatIncomeLevel(level: IncomeLevel): string {
  const labels: Record<IncomeLevel, string> = {
    low: 'Low Income',
    middle: 'Middle Income',
    high: 'High Income',
  }
  return labels[level]
}

/**
 * Format settlement type for display
 */
export function formatSettlementType(type: SettlementType): string {
  const labels: Record<SettlementType, string> = {
    rural: 'Rural',
    suburban: 'Suburban',
    urban: 'Urban',
  }
  return labels[type]
}

/**
 * Convert a breakdown record to chart data format
 */
export function toChartData<T extends string>(
  record: Record<T, number>,
  formatLabel: (key: T) => string,
  totalPopulation: number
): Array<{ name: string; value: number; percentage: number; key: T }> {
  return (Object.entries(record) as [T, number][])
    .map(([key, value]) => ({
      key,
      name: formatLabel(key),
      value,
      percentage: totalPopulation > 0 ? Math.round((value / totalPopulation) * 100) : 0,
    }))
    .sort((a, b) => b.value - a.value)
}
